import { useTranslation } from '@splitfiesta/i18n';

import { ErrorState, TranslatableError } from '@/types/error.types';

export class AppError extends Error {
  public readonly translationKey: string;
  public readonly translationParams?: Record<string, unknown>;

  constructor(
    translationKey: string,
    translationParams?: Record<string, unknown>
  ) {
    super(translationKey);
    this.translationKey = translationKey;
    this.translationParams = translationParams;
    this.name = 'AppError';
  }
}

export function createError(
  translationKey: string,
  translationParams?: Record<string, unknown>
): AppError {
  return new AppError(translationKey, translationParams);
}

export function handleError(error: unknown, namespace?: string): ErrorState {
  // Handle AppError (our custom translatable errors)
  if (error instanceof AppError) {
    return {
      key: error.translationKey,
      params: error.translationParams,
    };
  }

  // Handle API errors with translation support
  if (error && typeof error === 'object' && 'translationKey' in error) {
    const apiError = error as {
      translationKey: string;
      translationParams?: Record<string, unknown>;
    };
    return {
      key: apiError.translationKey,
      params: apiError.translationParams,
    };
  }

  // Handle API errors
  if (error instanceof Error) {
    return error.message;
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  // Fallback error
  return namespace
    ? `${namespace}:errors.unknown`
    : 'An unknown error occurred';
}

export function getErrorMessage(
  error: ErrorState,
  t: ReturnType<typeof useTranslation>['t']
): string {
  if (!error) return '';

  if (typeof error === 'string') {
    return error;
  }

  if (typeof error === 'object' && error.key) {
    // Handle special case where translation key starts with "errors."
    let finalKey = error.key;
    if (!error.key.includes(':') && error.key.startsWith('errors.')) {
      finalKey = error.key.replace('errors.', 'errors:');
    }
    return t(finalKey, error.params);
  }

  return 'An unknown error occurred';
}

export function isTranslatableError(
  error: ErrorState
): error is TranslatableError {
  return typeof error === 'object' && error !== null && 'key' in error;
}

export function createValidationError(
  field: string,
  translationKey: string,
  translationParams?: Record<string, unknown>
): AppError {
  return createError(translationKey, { field, ...translationParams });
}

export function createApiError(
  endpoint: string,
  translationKey: string,
  translationParams?: Record<string, unknown>
): AppError {
  return createError(translationKey, { endpoint, ...translationParams });
}
